import { NewsletterVariant } from "@product/scmp-sdk";
import capitalize from "lodash/capitalize";
import qs from "qs";
import { useCallback } from "react";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { sendGTMTracking } from "~/components/tracking/google-tag-manager/apis";
import { sendOnePlusXEvent } from "~/components/tracking/one-plus-x";
import { useParsedCookie } from "~/lib/hooks";
import { isSsr } from "~/lib/utils";

import type { NewsletterGASuccessParameter, NewsletterPayload } from "./types";

export const useNeedToShowNewsletterSignUpRedirect = () => {
  if (isSsr()) return false;
  const parsedQueryString = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  }) as unknown as NewsletterPayload;
  return parsedQueryString.subscription === "true" && parsedQueryString.newsletterIds?.length > 0;
};

export const useNewsletterTracking = () => {
  const { value: loginType } = useParsedCookie("scmp_user_login_type", raw => raw);

  const handleNewsletterSuccessTracking = useCallback(
    (parameter: NewsletterGASuccessParameter) => {
      parameter.payload.newsletterIds.forEach((id, index) => {
        const data = parameter.subscriptions?.find(element => element?.entityId === id);
        const handleTrackGA = () => {
          const sanitizedLoginType = capitalize(loginType ?? "");
          if (parameter.payload.campaignName) {
            // Send once for acquisition newsletter popup
            const isRequireToSendAcquisitionPopupEvent =
              parameter.payload.variant === NewsletterVariant.AcquisitionNewsletterPopup &&
              index === 0;
            if (isRequireToSendAcquisitionPopupEvent) {
              sendGATracking({
                action:
                  `Acquisition/${parameter.payload.campaignName}/Register/${sanitizedLoginType}` as const,
                category: "Acquisition",
                label: location.href,
              });
              sendGA4Tracking({
                action: "sys",
                category: "nl",
                customized_parameters: {
                  internal_campaign: parameter.payload.campaignName,
                  method: sanitizedLoginType.toLowerCase(),
                  nl_name: parameter.payload.newsletterName,
                  trigger_point: "ppto",
                },
                subcategory: "sign_up",
              });
            }
            return;
          }

          const label = parameter.isCombo
            ? "SCMP Global Impact, Hong Kong Update"
            : data?.entityLabel ?? "";
          sendGATracking({
            action: `Newsletter Sign Up/${sanitizedLoginType}` as const,
            category: parameter.eventCategory ?? "",
            label,
          });
        };
        handleTrackGA();
        sendGTMTracking({
          event: "newsletter-subscribe",
          "newsletter-id": data?.entityId ?? "",
          "newsletter-name": data?.entityLabel ?? "",
        });
        sendOnePlusXEvent({
          newsletterSubscribe: data?.entityLabel ?? "",
          type: "newsletter-sign-up",
        });
      });
    },
    [loginType],
  );

  return {
    handleNewsletterSuccessTracking,
  };
};
